import Finder from '../../../common/helpers/finder';
import { getHotspotDotRatio } from '../../hotspot/helpers/beaconStyle';
import { resolveTooltipPlacement, TOOLTIP_GAP } from '../../hotspot/helpers/tooltipPlacement';
import { printGuideLog, summarizePathsForLog } from '../helpers/guideLogHelper';
import { isGuideRecoveryDisabled } from '../helpers/guideRecoveryHelper';
import { HotspotTarget, MessageTypes } from '../types/MessageTypes';
import HotspotContainer from './HotspotContainer';

type TrackedBeacon = {
  target: HotspotTarget;
  element: HTMLElement | null;
  node: HTMLElement;
  lastRect: { x: number; y: number; width: number; height: number } | null;
  lastVisible: boolean | null;
  lastRecoveryAttemptAt: number;
  reportedLost: boolean;
};

/**
 * Draws the beacons on the host page.
 *
 * Beacons are host DOM rather than iframe content because many can be on
 * screen at once and none of them may block the page — an iframe per beacon
 * would be both heavy and click-blocking. A shadow root isolates the beacon
 * CSS from the customer's stylesheet in both directions.
 *
 * All beacons share a single `requestAnimationFrame` loop: N loops would
 * multiply the per-frame cost of a passive feature the user never asked for.
 */
class HotspotLayer {
  private static instance: HotspotLayer | null = null;
  private static readonly THROTTLE_MS = 25;
  private static readonly POSITION_THRESHOLD = 2;
  private static readonly RECOVERY_THROTTLE_MS = 300;

  private host: HTMLDivElement | null = null;
  private shadow: ShadowRoot | null = null;
  private beacons = new Map<string, TrackedBeacon>();
  private animationId: number | null = null;
  private lastCheckTime = 0;
  private hoveredPointId: string | null = null;
  private openTooltip: {
    pointId: string;
    width: number;
    height: number;
    positionType?: string;
  } | null = null;

  private constructor() {
    this.mount();
  }

  public static getInstance(): HotspotLayer {
    if (!HotspotLayer.instance) {
      HotspotLayer.instance = new HotspotLayer();
    }
    return HotspotLayer.instance;
  }

  private mount(): void {
    this.host = document.createElement('div');
    this.host.toggleAttribute('getdemo-hotspot-layer', true);
    this.host.style.setProperty('position', 'fixed');
    this.host.style.setProperty('top', '0');
    this.host.style.setProperty('left', '0');
    this.host.style.setProperty('width', '0');
    this.host.style.setProperty('height', '0');
    this.host.style.setProperty('z-index', '2147483643');
    // The layer itself must never swallow a click: only the beacons do, and
    // they re-enable pointer events on themselves.
    this.host.style.setProperty('pointer-events', 'none');

    this.shadow = this.host.attachShadow({ mode: 'open' });
    const style = document.createElement('style');
    style.textContent = `
      :host { all: initial; }
      .gd-hotspot-beacon {
        position: fixed;
        border-radius: 9999px;
        border-style: solid;
        border-width: 2px;
        background-color: transparent;
        cursor: pointer;
        pointer-events: auto;
        display: grid;
        place-items: center;
        box-sizing: border-box;
        transform-origin: center;
      }
      .gd-hotspot-beacon .gd-hotspot-dot {
        border-radius: 9999px;
        pointer-events: none;
      }
      .gd-hotspot-beacon::before {
        content: '';
        position: absolute;
        inset: 0;
        border-radius: 9999px;
        background-color: var(--hotspot-beacon-color, currentColor);
        opacity: 0.4;
        animation: gd-hotspot-pulse 2s ease-out infinite;
        pointer-events: none;
      }
      @keyframes gd-hotspot-pulse {
        0% { transform: scale(1); opacity: 0.4; }
        70% { transform: scale(1.75); opacity: 0; }
        100% { transform: scale(1.75); opacity: 0; }
      }
      @media (prefers-reduced-motion: reduce) {
        .gd-hotspot-beacon::before { animation: none; }
      }
    `;
    this.shadow.appendChild(style);
    document.documentElement.appendChild(this.host);
  }

  public setTargets(targets: HotspotTarget[]): void {
    const incomingIds = new Set(targets.map(target => target.pointId));

    this.beacons.forEach((beacon, pointId) => {
      if (!incomingIds.has(pointId)) {
        beacon.node.remove();
        this.beacons.delete(pointId);
      }
    });

    targets.forEach(target => {
      const existing = this.beacons.get(target.pointId);
      if (existing) {
        existing.target = target;
        this.styleBeacon(existing.node, target);
        return;
      }
      this.beacons.set(target.pointId, {
        target,
        element: this.resolveElement(target),
        node: this.createBeaconNode(target),
        lastRect: null,
        lastVisible: null,
        lastRecoveryAttemptAt: 0,
        reportedLost: false
      });
    });

    printGuideLog({
      message: '[HotspotLayer] setTargets',
      data: { count: targets.length }
    });

    if (this.beacons.size > 0) {
      this.startMonitoring();
    } else {
      this.stopMonitoring();
    }
  }

  public dismiss(pointId: string): void {
    const beacon = this.beacons.get(pointId);
    if (!beacon) return;
    beacon.node.remove();
    this.beacons.delete(pointId);
    if (this.hoveredPointId === pointId) {
      this.hoveredPointId = null;
      this.openTooltip = null;
      HotspotContainer.getInstance().hide();
    }
    if (this.beacons.size === 0) this.stopMonitoring();
  }

  /**
   * Places the tooltip iframe next to the hovered beacon. Runs on the host
   * because the player iframe is sized to the tooltip itself and cannot see
   * where the beacon sits in the page.
   */
  public resizeTooltip(message: {
    pointId?: string;
    positionType?: string;
    position?: { top: string; left: string };
    dimensions: { width: string; height: string };
    show?: boolean;
  }): void {
    const container = HotspotContainer.getInstance();

    if (!message.show) {
      this.openTooltip = null;
      container.resizeAndPosition({ top: '0px', left: '0px' }, { width: '0px', height: '0px' });
      container.hide();
      return;
    }

    const width = Number.parseFloat(message.dimensions?.width);
    const height = Number.parseFloat(message.dimensions?.height);
    if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
      return;
    }

    const beacon = message.pointId ? this.beacons.get(message.pointId) : undefined;
    let position = message.position;

    this.openTooltip = beacon
      ? { pointId: beacon.target.pointId, width, height, positionType: message.positionType }
      : null;

    if (beacon) {
      position = this.resolvePositionFor(beacon, width, height, message.positionType) ?? position;
    }

    if (!position) return;

    container.resizeAndPosition(position, {
      width: `${width}px`,
      height: `${height}px`
    });
    container.show();
  }

  private createBeaconNode(target: HotspotTarget): HTMLElement {
    const node = document.createElement('div');
    node.className = 'gd-hotspot-beacon';
    node.setAttribute('role', 'button');
    node.setAttribute('tabindex', '0');
    node.dataset.pointId = target.pointId;

    const dot = document.createElement('div');
    dot.className = 'gd-hotspot-dot';
    node.appendChild(dot);

    this.styleBeacon(node, target);
    node.style.setProperty('display', 'none');

    const enter = () => this.handleEnter(target.pointId);
    node.addEventListener('mouseenter', enter);
    node.addEventListener('focus', enter);
    node.addEventListener('mouseleave', () => this.handleLeave(target.pointId));
    node.addEventListener('blur', () => this.handleLeave(target.pointId));
    node.addEventListener('click', event => {
      event.stopPropagation();
      this.handleEnter(target.pointId);
    });

    this.shadow?.appendChild(node);
    return node;
  }

  private styleBeacon(node: HTMLElement, target: HotspotTarget): void {
    const size = target.beaconSize || 20;
    const dotSize = size * getHotspotDotRatio(target.beaconStyle);
    node.style.setProperty('width', `${size}px`);
    node.style.setProperty('height', `${size}px`);
    node.style.setProperty('border-color', target.color);
    node.style.setProperty('background-color', 'transparent');
    node.style.setProperty('--hotspot-beacon-color', target.color);
    const dot = node.querySelector('.gd-hotspot-dot') as HTMLElement | null;
    if (dot) {
      dot.style.setProperty('background-color', target.color);
      dot.style.setProperty('width', `${dotSize}px`);
      dot.style.setProperty('height', `${dotSize}px`);
    }
  }

  private handleEnter(pointId: string): void {
    this.hoveredPointId = pointId;
    const beacon = this.beacons.get(pointId);
    if (!beacon?.element) return;

    HotspotContainer.getInstance().sendMessage({
      type: MessageTypes.HOTSPOT_ENTER,
      pointId
    });
  }

  private handleLeave(pointId: string): void {
    // The player owns the grace period: the pointer usually crosses dead space
    // between the beacon and the tooltip, and closing here would make the
    // tooltip unreachable.
    HotspotContainer.getInstance().sendMessage({
      type: MessageTypes.HOTSPOT_LEAVE,
      pointId
    });
  }

  private resolvePositionFor(
    beacon: TrackedBeacon,
    width: number,
    height: number,
    positionType?: string
  ): { top: string; left: string } | null {
    const anchorNode =
      beacon.node.style.display !== 'none' && beacon.node.isConnected
        ? beacon.node
        : beacon.element;
    if (!anchorNode) return null;

    const rect = anchorNode.getBoundingClientRect();
    const { top, left } = resolveTooltipPlacement({
      anchor: { x: rect.x, y: rect.y, width: rect.width, height: rect.height },
      tooltip: { width, height },
      viewport: { width: window.innerWidth, height: window.innerHeight },
      positionType: positionType ?? beacon.target.positionType,
      gap: TOOLTIP_GAP
    });
    return { top: `${top}px`, left: `${left}px` };
  }

  /**
   * Keeps an open tooltip glued to its beacon while the page scrolls or
   * reflows. The player cannot do this itself: its iframe is sized to the
   * tooltip, so it never learns that the anchor moved.
   */
  private repositionOpenTooltip(beacon: TrackedBeacon): void {
    const open = this.openTooltip;
    if (!open || open.pointId !== beacon.target.pointId) return;

    const position = this.resolvePositionFor(beacon, open.width, open.height, open.positionType);
    if (!position) return;

    HotspotContainer.getInstance().resizeAndPosition(position, {
      width: `${open.width}px`,
      height: `${open.height}px`
    });
  }

  private resolveElement(target: HotspotTarget): HTMLElement | null {
    try {
      if (target.customPath) {
        return document.querySelector(target.customPath) as HTMLElement | null;
      }
      if (!Array.isArray(target.paths) || target.paths.length === 0) return null;
      const { mainPaths } = Finder.splitPathsByIframe(target.paths);
      return Finder.findElementByPaths(mainPaths) as HTMLElement | null;
    } catch {
      return null;
    }
  }

  private tryRecover(beacon: TrackedBeacon, currentTime: number): void {
    if (!beacon.reportedLost) {
      beacon.reportedLost = true;
      printGuideLog({
        message: '[HotspotLayer] positionMonitor:elementLost',
        data: {
          pointId: beacon.target.pointId,
          pathSummary: summarizePathsForLog(beacon.target.paths ?? [])
        },
        level: 'warn'
      });
    }

    // An absent anchor just hides its beacon: a hotspot is decoration, and a
    // beacon floating over unrelated content is worse than no beacon.
    beacon.node.style.setProperty('display', 'none');

    if (isGuideRecoveryDisabled()) return;
    if (currentTime - beacon.lastRecoveryAttemptAt < HotspotLayer.RECOVERY_THROTTLE_MS) return;
    beacon.lastRecoveryAttemptAt = currentTime;

    const found = this.resolveElement(beacon.target);
    if (!found || !found.isConnected) return;

    beacon.element = found;
    beacon.reportedLost = false;
    beacon.lastRect = null;
    beacon.lastVisible = null;
    printGuideLog({
      message: '[HotspotLayer] positionMonitor:recoveredElement',
      data: { pointId: beacon.target.pointId, tag: found.tagName }
    });
  }

  private positionBeacon(beacon: TrackedBeacon, rect: DOMRect): void {
    const size = beacon.target.beaconSize || 20;
    beacon.node.style.setProperty('display', 'grid');
    beacon.node.style.setProperty('top', `${rect.top + rect.height / 2 - size / 2}px`);
    beacon.node.style.setProperty('left', `${rect.left + rect.width / 2 - size / 2}px`);
  }

  private startMonitoring(): void {
    if (this.animationId !== null) return;

    const tick = (currentTime: number) => {
      if (currentTime - this.lastCheckTime < HotspotLayer.THROTTLE_MS) {
        this.animationId = requestAnimationFrame(tick);
        return;
      }
      this.lastCheckTime = currentTime;

      this.beacons.forEach(beacon => {
        if (!beacon.element || !beacon.element.isConnected) {
          this.tryRecover(beacon, currentTime);
          return;
        }

        const rect = beacon.element.getBoundingClientRect();
        const visible = Finder.isElementVisible(beacon.element);

        const moved =
          !beacon.lastRect ||
          Math.abs(rect.x - beacon.lastRect.x) > HotspotLayer.POSITION_THRESHOLD ||
          Math.abs(rect.y - beacon.lastRect.y) > HotspotLayer.POSITION_THRESHOLD ||
          Math.abs(rect.width - beacon.lastRect.width) > HotspotLayer.POSITION_THRESHOLD ||
          Math.abs(rect.height - beacon.lastRect.height) > HotspotLayer.POSITION_THRESHOLD;
        const visibilityChanged = beacon.lastVisible !== visible;

        if (moved || visibilityChanged) {
          if (visible) {
            this.positionBeacon(beacon, rect);
          } else {
            beacon.node.style.setProperty('display', 'none');
          }
          this.repositionOpenTooltip(beacon);
        }

        beacon.lastRect = { x: rect.x, y: rect.y, width: rect.width, height: rect.height };
        beacon.lastVisible = visible;
      });

      this.animationId = requestAnimationFrame(tick);
    };

    this.animationId = requestAnimationFrame(tick);
  }

  private stopMonitoring(): void {
    if (this.animationId !== null) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
    this.lastCheckTime = 0;
  }

  public clear(): void {
    this.beacons.forEach(beacon => beacon.node.remove());
    this.beacons.clear();
    this.hoveredPointId = null;
    this.openTooltip = null;
    this.stopMonitoring();
  }

  public destroy(): void {
    this.clear();
    if (this.host && document.documentElement.contains(this.host)) {
      document.documentElement.removeChild(this.host);
    }
    this.host = null;
    this.shadow = null;
    HotspotLayer.instance = null;
  }

  public static destroy(): void {
    HotspotLayer.instance?.destroy();
  }
}

export default HotspotLayer;
